<template>
  <div
    id="bottom-bars-container"
    ref="containerRef"
    class="bottom-bars-container"
    :class="{ 'sidebar-collapsed': sidebarCollapsed }"
  >
    <slot />
  </div>
</template>

<script setup>
// Container pour les bandeaux en bas de page (édition et appel)
// Les bandeaux sont téléportés ici via Teleport
// Le container est toujours présent pour garantir que l'élément existe pour Teleport
import { ref, onMounted, onBeforeUnmount } from 'vue'

defineProps({
  sidebarCollapsed: {
    type: Boolean,
    default: false
  }
})

// Expose la hauteur occupée par les bandeaux fixes via --bottom-bars-height,
// pour que les pages puissent dégager leur contenu / barre d'action collante.
// 0 quand aucun bandeau n'est téléporté (le container vide est transparent).
const containerRef = ref(null)
let resizeObserver = null

const updateBottomBarsHeight = () => {
  const el = containerRef.value
  if (!el) return
  const hasBars = el.children.length > 0
  const height = hasBars ? Math.round(el.getBoundingClientRect().height) : 0
  document.documentElement.style.setProperty('--bottom-bars-height', `${height}px`)
}

onMounted(() => {
  updateBottomBarsHeight()
  resizeObserver = new ResizeObserver(updateBottomBarsHeight)
  resizeObserver.observe(containerRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  document.documentElement.style.removeProperty('--bottom-bars-height')
})
</script>

<style scoped>
.bottom-bars-container {
  position: fixed;
  bottom: 0;
  left: var(--sidebar-width);
  right: 0;
  z-index: var(--z-fixed);
  display: flex;
  flex-direction: column-reverse; /* Le bandeau d'appel en bas, celui d'édition au-dessus */
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  pointer-events: none; /* Permet les clics à travers, sauf sur les enfants */
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Rail de la barre repliée : 96 px. */
.bottom-bars-container.sidebar-collapsed {
  left: 96px;
}

.bottom-bars-container > * {
  pointer-events: auto; /* Réactive les clics sur les enfants */
}

@media (max-width: 768px) {
  .bottom-bars-container {
    left: 0;
  }
}
</style>

